<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'account',
    path: '/accounts/:id',
    component: 'Account',
    params: [
      {
        name: 'id',
        prop: 'oid',
      },
    ],
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'Account',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'saved']);

  const props = defineProps({
    ...userComponentProps,
    oid: {
      type: String,
      required: false,
      default: null,
    },
    ownerId: {
      type: String,
      required: false,
      default: null,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mainForm = ref(null);

  const $refs = {
    get mainForm() {
      return mainForm.value;
    },
  };

  const formId = ref(null);
  const item = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));

  const $data = computed(() => ({
    formId: formId.value,
    item: item.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_item = () => {
    return props.oid == null
      ? $fn.value.schemaDefaults('CreateAccount', {
          owner: { id: props.ownerId },
        })
      : $fn.value.fetch('read_account', {
          asResource: true,
          parameters: { id: props.oid },
        });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formId' },
      { name: 'item' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: { formId: $$load_formId, item: $$load_item },
    ux: {},
    dataRefs: {
      formId,
      item,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    if (loaded.value) {
      return {
        title:
          props.oid == null
            ? $fn.value.gettext('New Cash or Bank Account')
            : [item.value.snapshot('name'), $fn.value.gettext('Account')],
      };
    }
    return null;
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );
  watch(
    () => props.ownerId,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('ownerId');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_14r42zaec_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_6466705138206980_reloadGrid_vh87m07p4_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'payments',
    };
  };
  const act_1040167445267876_setData_5ulvlrbci_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'item',
      value: $event.data,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_1040167445267876_setData_5ulvlrbci_getWhen = async ($event) => {
    return !!$event.data;
  };
  const act_1040167445267876_emit_vom93k9sb_getActionArgs = async ($event) => {
    return {
      event: 'saved',
      value: $event.data,
    };
  };
  const act_1040167445267876_closeModal_cnqzeeio8_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data,
      },
    };
  };
  const inst_evh_1040167445267876_success_o8ei58s1w_getWhen = async (
    $event
  ) => {
    return props.modal;
  };
  const act_1040167445267876_reloadGrid_rrf6hkpfn_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'accounts',
    };
  };
  const act_1040167445267876_crud_oacrtnw1y_getActionArgs = async ($event) => {
    return {
      objectType: 'account',
      op: 'upsert',
      data: item.value,
    };
  };
  const inst_evh_7789867021953041_saved_wsmkapord_getWhen = async ($event) => {
    return props.modal;
  };
  const act_7789867021953041_reloadGrid_tulzflc03_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'accounts',
    };
  };
  const act_2434514588044777_closeComponent_s9v0bl0sz_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        close: true,
      },
    };
  };

  const act_7315092382398562_closeModal_14r42zaec =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_14r42zaec_getActionArgs,
    });
  const inst_evh_7315092382398562_close_pq36xpvpf =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_14r42zaec,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_pq36xpvpf],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_5w5ziad3j =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_rsd0r4ltx =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_5w5ziad3j,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_rsd0r4ltx],
    event: 'reload',
  });
  const act_8564662037462133_closeModal_f5knqkjv3 =
    new actionTypes.CloseModalAction({});
  const inst_evh_8564662037462133_close_1lpvqzxku =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeModal_f5knqkjv3,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_1lpvqzxku],
    event: 'close',
  });
  const act_6466705138206980_reloadComponentData_mmem9t8eo =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_6466705138206980_reload_vsl9drxa4 =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_reloadComponentData_mmem9t8eo,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_6466705138206980_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6466705138206980_reload_vsl9drxa4],
    event: 'reload',
  });
  const act_6466705138206980_closeComponent_7hczomoxs =
    new actionTypes.CloseComponentAction({});
  const inst_evh_6466705138206980_deleted_ccp58teja =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_closeComponent_7hczomoxs,
      event: 'deleted',
      displayName: 'closeComponent',
    });
  const act_6466705138206980_reloadGrid_vh87m07p4 =
    new actionTypes.ReloadGridAction({
      actionArgs: act_6466705138206980_reloadGrid_vh87m07p4_getActionArgs,
    });
  const inst_evh_6466705138206980_deleted_ceryb7x8d =
    new actionTypes.EventHandlerImpl({
      action: act_6466705138206980_reloadGrid_vh87m07p4,
      event: 'deleted',
      displayName: 'reloadGrid',
    });
  const inst_evh_6466705138206980_deleted = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_6466705138206980_deleted_ccp58teja,
      inst_evh_6466705138206980_deleted_ceryb7x8d,
    ],
    event: 'deleted',
  });
  const act_1040167445267876_setData_5ulvlrbci = new actionTypes.SetDataAction({
    actionArgs: act_1040167445267876_setData_5ulvlrbci_getActionArgs,
    when: act_1040167445267876_setData_5ulvlrbci_getWhen,
  });
  const inst_evh_1040167445267876_success_9605ugy90 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_setData_5ulvlrbci,
      event: 'success',
      displayName: 'setData',
    });
  const act_1040167445267876_emit_vom93k9sb = new actionTypes.EmitAction({
    actionArgs: act_1040167445267876_emit_vom93k9sb_getActionArgs,
  });
  const inst_evh_1040167445267876_success_wqdfrfle5 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_emit_vom93k9sb,
      event: 'success',
      displayName: 'emit',
    });
  const act_1040167445267876_closeModal_cnqzeeio8 =
    new actionTypes.CloseModalAction({
      actionArgs: act_1040167445267876_closeModal_cnqzeeio8_getActionArgs,
    });
  const inst_evh_1040167445267876_success_o8ei58s1w =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_closeModal_cnqzeeio8,
      event: 'success',
      when: inst_evh_1040167445267876_success_o8ei58s1w_getWhen,
      displayName: 'closeModal',
    });
  const act_1040167445267876_reloadGrid_rrf6hkpfn =
    new actionTypes.ReloadGridAction({
      actionArgs: act_1040167445267876_reloadGrid_rrf6hkpfn_getActionArgs,
    });
  const inst_evh_1040167445267876_success_lngo1tc9g =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_reloadGrid_rrf6hkpfn,
      event: 'success',
      displayName: 'reloadGrid',
    });
  const act_1040167445267876_crud_oacrtnw1y = new actionTypes.CRUDAction({
    actionArgs: act_1040167445267876_crud_oacrtnw1y_getActionArgs,
    displayName: 'save',
    events: [
      inst_evh_1040167445267876_success_9605ugy90,
      inst_evh_1040167445267876_success_wqdfrfle5,
      inst_evh_1040167445267876_success_o8ei58s1w,
      inst_evh_1040167445267876_success_lngo1tc9g,
    ],
  });
  const inst_evh_1040167445267876_submit_pisanlbu6 =
    new actionTypes.EventHandlerImpl({
      action: act_1040167445267876_crud_oacrtnw1y,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_1040167445267876_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1040167445267876_submit_pisanlbu6],
    event: 'submit',
  });
  const act_7789867021953041_closeModal_icq0j7lqg =
    new actionTypes.CloseModalAction({});
  const inst_evh_7789867021953041_saved_wsmkapord =
    new actionTypes.EventHandlerImpl({
      action: act_7789867021953041_closeModal_icq0j7lqg,
      event: 'saved',
      when: inst_evh_7789867021953041_saved_wsmkapord_getWhen,
      displayName: 'closeModal',
    });
  const act_7789867021953041_reloadGrid_tulzflc03 =
    new actionTypes.ReloadGridAction({
      actionArgs: act_7789867021953041_reloadGrid_tulzflc03_getActionArgs,
    });
  const inst_evh_7789867021953041_saved_filgpxv26 =
    new actionTypes.EventHandlerImpl({
      action: act_7789867021953041_reloadGrid_tulzflc03,
      event: 'saved',
      displayName: 'reloadGrid',
    });
  const inst_evh_7789867021953041_saved = new actionTypes.EventHandlerGroup({
    handlers: [
      inst_evh_7789867021953041_saved_wsmkapord,
      inst_evh_7789867021953041_saved_filgpxv26,
    ],
    event: 'saved',
  });
  const act_2434514588044777_closeComponent_s9v0bl0sz =
    new actionTypes.CloseComponentAction({
      actionArgs: act_2434514588044777_closeComponent_s9v0bl0sz_getActionArgs,
    });
  const inst_evh_2434514588044777_clickPreventStop_g14cybw8x =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_s9v0bl0sz,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_g14cybw8x],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_pq36xpvpf,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_rsd0r4ltx,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_1lpvqzxku,
    inst_evh_8564662037462133_close,
    inst_evh_6466705138206980_reload_vsl9drxa4,
    inst_evh_6466705138206980_reload,
    inst_evh_6466705138206980_deleted_ccp58teja,
    inst_evh_6466705138206980_deleted_ceryb7x8d,
    inst_evh_6466705138206980_deleted,
    inst_evh_1040167445267876_success_9605ugy90,
    inst_evh_1040167445267876_success_wqdfrfle5,
    inst_evh_1040167445267876_success_o8ei58s1w,
    inst_evh_1040167445267876_success_lngo1tc9g,
    inst_evh_1040167445267876_submit_pisanlbu6,
    save: inst_evh_1040167445267876_submit_pisanlbu6,
    inst_evh_1040167445267876_submit,
    inst_evh_7789867021953041_saved_wsmkapord,
    inst_evh_7789867021953041_saved_filgpxv26,
    inst_evh_7789867021953041_saved,
    inst_evh_2434514588044777_clickPreventStop_g14cybw8x,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_pq36xpvpf = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_pq36xpvpf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_rsd0r4ltx = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_rsd0r4ltx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_1lpvqzxku = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_1lpvqzxku.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_reload_vsl9drxa4 = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_reload_vsl9drxa4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_reload = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted_ccp58teja = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted_ccp58teja.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted_ceryb7x8d = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted_ceryb7x8d.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6466705138206980_deleted = async (event, scope) => {
    return await actions.inst_evh_6466705138206980_deleted.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_9605ugy90 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_9605ugy90.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_wqdfrfle5 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_wqdfrfle5.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_o8ei58s1w = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_o8ei58s1w.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_success_lngo1tc9g = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_success_lngo1tc9g.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit_pisanlbu6 = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit_pisanlbu6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1040167445267876_submit = async (event, scope) => {
    return await actions.inst_evh_1040167445267876_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_saved_wsmkapord = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_saved_wsmkapord.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_saved_filgpxv26 = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_saved_filgpxv26.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7789867021953041_saved = async (event, scope) => {
    return await actions.inst_evh_7789867021953041_saved.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_g14cybw8x = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_g14cybw8x.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="Account"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
        <UcAccountHeader
          :account="item"
          @reload="evh_6466705138206980_reload($event, {})"
          @deleted="evh_6466705138206980_deleted($event, {})"
        >
        </UcAccountHeader>
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm
        :id="formId"
        name="account"
        :readonly="item.locked"
        class="row"
        ref="mainForm"
        @submit="evh_1040167445267876_submit($event, {})"
      >
        <ItpCol>
          <FieldsetAccountGeneral
            :item="item"
            :formId="formId"
            no-submit-button
            @saved="evh_7789867021953041_saved($event, {})"
          >
          </FieldsetAccountGeneral>
        </ItpCol>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <ItpButton
          type="submit"
          :text="gettext('Save')"
          :form="forms.account"
          :spinning="actions.save.isRunning"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Close')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
